import { useLiveQuery } from '@electric-sql/pglite-react';
import { evaluatePlan, type LabPlan, localDay, type PlanStatus } from '@quickreport/domain';
import { kvGet, kvSet, OfflineError } from '@quickreport/sync';
import {
  listReminders,
  type Reminder,
  readKeys,
  markRead as saveRead,
  markUnread as saveUnread,
} from '@quickreport/workspace';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth, useSyncStatus } from '../auth/AuthProvider';
import { api } from './api';
import { type PlanNotice, planNotice } from './plan';
import { useWorkspace } from './workspace';

/** A message from the LabView team. */
export interface Message {
  id: string;
  title: string;
  body: string;
  kind: 'info' | 'payment' | 'urgent';
  createdAt: string;
  key: string;
}

interface Notifications {
  reminders: Reminder[];
  messages: Message[];
  isRead: (key: string) => boolean;
  /** Reminders and messages not yet read. */
  unread: number;
  unreadReminders: number;
  unreadMessages: number;
  markRead: (keys: string[]) => Promise<void>;
  markUnread: (keys: string[]) => Promise<void>;
  /** Re-count after a change made on this screen. */
  refresh: () => void;
  /** Today in the person's calendar; changes at midnight. */
  today: string;
  /** The lab's plan as last heard from the server (kept for when the computer is offline). */
  plan: LabPlan | null;
  planStatus: PlanStatus;
  /** Something to tell the lab about its plan, or null. */
  planNotice: PlanNotice | null;
}

const Ctx = createContext<Notifications | null>(null);

const MESSAGES_KEY = 'broadcasts';
const PLAN_KEY = 'plan';
const POLL_MS = 5 * 60_000;

/**
 * One place that knows what needs attention: reports due today or late, and messages from the
 * LabView team. Reminders come from this computer's own data, so they work offline; messages are
 * fetched when online and remembered for when it is not.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const ws = useWorkspace();
  const { blockNow } = useAuth();
  const sync = useSyncStatus(ws.engine);
  const [today, setToday] = useState(localDay());
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [plan, setPlan] = useState<LabPlan | null>(null);
  const [read, setRead] = useState<Set<string>>(new Set());
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  // The day changes at midnight even if nobody touches the app.
  useEffect(() => {
    const t = setInterval(() => setToday(localDay()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Anything that could change who is due: a report's due date, status or completion.
  const stamp = useLiveQuery<{ k: string }>(
    `select concat_ws('|', count(*), max(updated_at)) as k from reports
      where deleted_at is null and due_date is not null and due_date <= $1::date`,
    [today],
  );
  const key = stamp?.rows[0]?.k ?? '';

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` and `tick` are the signals that something changed
  useEffect(() => {
    let alive = true;
    void (async () => {
      const [r, keys] = await Promise.all([listReminders(ws.db, today), readKeys(ws.db)]);
      if (!alive) return;
      setReminders(r);
      setRead(keys);
    })();
    return () => {
      alive = false;
    };
  }, [ws.db, today, key, tick, sync.lastSyncedAt]);

  const loadSaved = useCallback(async () => {
    try {
      const raw = await kvGet(ws.db, MESSAGES_KEY);
      const list = raw ? (JSON.parse(raw) as Omit<Message, 'key'>[]) : [];
      setMessages(list.map((m) => ({ ...m, key: `msg:${m.id}` })));
    } catch {
      setMessages([]);
    }
    try {
      const raw = await kvGet(ws.db, PLAN_KEY);
      setPlan(raw ? (JSON.parse(raw) as LabPlan | null) : null);
    } catch {
      setPlan(null);
    }
  }, [ws.db]);

  // Messages: show what was saved at once, then ask the server now and every few minutes.
  useEffect(() => {
    void loadSaved();
    let alive = true;
    const poll = async () => {
      try {
        const r = await api<{
          blocked: { kind: 'account' | 'lab'; message: string } | null;
          broadcasts: Omit<Message, 'key'>[];
          plan?: LabPlan | null;
        }>('GET', '/v1/notifications');
        if (!alive) return;
        if (r.blocked) return blockNow({ kind: r.blocked.kind, message: r.blocked.message });
        await kvSet(ws.db, MESSAGES_KEY, JSON.stringify(r.broadcasts));
        await kvSet(ws.db, PLAN_KEY, JSON.stringify(r.plan ?? null));
        await loadSaved();
      } catch (err) {
        // Offline, or the server is busy: keep what is saved and try again later.
        if (!(err instanceof OfflineError)) console.warn('[notifications]', err);
      }
    };
    void poll();
    const t = setInterval(() => void poll(), POLL_MS);
    const onOnline = () => void poll();
    window.addEventListener('online', onOnline);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener('online', onOnline);
    };
  }, [ws.db, loadSaved, blockNow]);

  const markRead = useCallback(
    async (keys: string[]) => {
      await saveRead(ws.db, keys);
      setRead(await readKeys(ws.db));
    },
    [ws.db],
  );
  const markUnread = useCallback(
    async (keys: string[]) => {
      await saveUnread(ws.db, keys);
      setRead(await readKeys(ws.db));
    },
    [ws.db],
  );

  const value = useMemo<Notifications>(() => {
    // The plan is judged against today's date here, so it is right offline too.
    const planStatus = evaluatePlan(plan, new Date());
    const notice = planNotice(plan, planStatus);
    const all: Message[] = notice
      ? [
          {
            id: notice.key,
            key: notice.key,
            title: notice.title,
            body: notice.body,
            kind: notice.tone === 'alert' ? 'urgent' : 'payment',
            createdAt: new Date().toISOString(),
          },
          ...messages,
        ]
      : messages;
    const unreadReminders = reminders.filter((r) => !read.has(r.key)).length;
    const unreadMessages = all.filter((m) => !read.has(m.key)).length;
    return {
      reminders,
      messages: all,
      plan,
      planStatus,
      planNotice: notice,
      isRead: (k) => read.has(k),
      unread: unreadReminders + unreadMessages,
      unreadReminders,
      unreadMessages,
      markRead,
      markUnread,
      refresh,
      today,
    };
  }, [reminders, messages, plan, read, markRead, markUnread, refresh, today]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useNotifications(): Notifications {
  const v = useContext(Ctx);
  if (!v) throw new Error('useNotifications outside NotificationsProvider');
  return v;
}
